import { ReactComponent as ArrowDownIcon } from 'Assets/icons/arrow-down.svg';
import * as React from 'react';
import { pushToGTMDataLayer } from 'Utils/analytics';
import { IconButton } from '~/lib/ui/icon-button';
import { ScrollToBottomFABProps } from './index.types';

const SCROLL_THRESHOLD = 50;
const HIDE_BUTTON_DELAY = 200;
const DEBOUNCE_DELAY = 100;

const debounce = (func: () => void, delay: number) => {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  return () => {
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(func, delay);
  };
};

export const ScrollToBottomFAB: React.FC<ScrollToBottomFABProps> = ({
  scrollContainerRef,
  onScrollToBottom,
  ariaLabel = 'Scroll to latest message',
  className,
  style,
}) => {
  const [showScrollToBottom, setShowScrollToBottom] = React.useState(false);

  const handleScroll = React.useCallback(() => {
    const ref = scrollContainerRef.current;

    if (!ref) {
      return;
    }

    const { scrollTop, scrollHeight, clientHeight } = ref;
    const atBottom = scrollHeight - scrollTop - clientHeight < SCROLL_THRESHOLD;

    setShowScrollToBottom(!atBottom);
  }, [scrollContainerRef]);

  const debouncedHandleScroll = React.useMemo(
    () => debounce(handleScroll, DEBOUNCE_DELAY),
    [handleScroll]
  );

  React.useEffect(() => {
    const ref = scrollContainerRef.current;
    if (!ref) {
      return;
    }

    ref.addEventListener('scroll', handleScroll);
    handleScroll();

    const resizeObserver = new ResizeObserver(debouncedHandleScroll);
    const mutationObserver = new MutationObserver(debouncedHandleScroll);

    resizeObserver.observe(ref);
    mutationObserver.observe(ref, {
      childList: true,
      subtree: true,
      attributes: false,
    });

    return () => {
      ref.removeEventListener('scroll', handleScroll);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [handleScroll, debouncedHandleScroll, scrollContainerRef]);

  const handleScrollToBottom = React.useCallback(
    (e?: React.MouseEvent | React.KeyboardEvent) => {
      if (e) {
        e.preventDefault();
      }

      const ref = scrollContainerRef.current;
      if (ref) {
        ref.scrollTo({
          top: ref.scrollHeight,
          behavior: 'smooth',
        });

        setTimeout(() => setShowScrollToBottom(false), HIDE_BUTTON_DELAY);
        pushToGTMDataLayer('chat_bottom_FAB');
        onScrollToBottom?.();
      }
    },
    [onScrollToBottom, scrollContainerRef]
  );

  if (!showScrollToBottom) {
    return null;
  }

  return (
    <div
      className={`absolute left-1/2 transform -translate-x-1/2 bottom-3 z-1 ${
        className || ''
      }`}
      style={style}
    >
      <IconButton
        icon={ArrowDownIcon}
        label=""
        title="Scroll to bottom"
        onClick={handleScrollToBottom}
        iconSize={24}
        variant="outline"
        testID="scroll-to-bottom-fab"
      />
    </div>
  );
};

export default ScrollToBottomFAB;
